// The ONE seam between the frontend and data. Every module calls through
// here, never directly through fetch/axios scattered across components,
// and NEVER directly to MyStocks Africa (no MSA credential ever reaches
// this bundle — see README.md "Security notes").
//
// Modules are LIVE against the backend unless their `LIVE` flag below is
// false — those still resolve against each module's `*.mock.js` fixtures
// (kept for offline/dev only). The `request()` helper is wired for the live
// path (auth header, base URL, error normalisation).

import { getIdToken } from '../context/AuthContext.jsx';
import { assertBacktestExecutionCosts } from '../utils/engineConfig.js';
import { assertValidBacktestPeriods } from '../utils/backtestPeriod.js';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

// Flip modules to live one at a time as backend + MSA wiring lands. Kept
// as a single flag map so a partial rollout (e.g. briefings live, screener
// still mock) is a one-line change here rather than scattered edits.
export const LIVE = {
  msa: true, // live MyStocks Africa market data (server-side MSA_QAPTAL_DATA)
  backtest: true, // real open_backtester engine (server-side OBT_API_KEY)
  portfolio: true, // portfolio construction + risk analytics (server DB + MSA OHLCV)
  research: true, // real ML research (server-side OBT_API_KEY; GPU requires gpu tier)
};

async function request(path, { method = 'GET', body, auth = false } = {}) {
  const submission = method === 'POST' && /^\/(backtest\/(run|optimize|montecarlo|walkforward|portfolio|search)|research\/(train|sweep))$/.test(path);
  const submissionKey = submission ? crypto.randomUUID() : null;
  async function attempt(withFreshToken, waitMs = 0) {
    if (waitMs) await new Promise((r) => setTimeout(r, waitMs));
    const headers = { 'Content-Type': 'application/json' };
    if (submissionKey) headers['Idempotency-Key'] = submissionKey;
    if (auth) {
      const token = await getIdToken(withFreshToken ? { forceRefresh: true } : undefined);
      if (!token && !withFreshToken) {
        // Auth state resolved but the ID token isn't minted yet (Firebase
        // fires onAuthStateChanged before the first token is ready). Wait
        // briefly and retry — avoids the 401-then-recover console noise.
        return attempt(true, 400);
      }
      if (token) headers.Authorization = `Bearer ${token}`;
    }
    const resp = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
      cache: auth ? 'no-store' : 'default',
    });
    if (resp.status === 401 && auth && !withFreshToken) {
      // Expired/invalid ID token — force a fresh one and retry exactly once.
      return attempt(true);
    }
    if (!resp.ok) {
      const errBody = await resp.json().catch(() => ({}));
      const err = new Error(errBody.error || `request_failed_${resp.status}`);
      err.status = resp.status;
      err.detail = errBody.detail;
      throw err;
    }
    if (resp.status === 204) return null;
    const data = await resp.json();
    if (submission && data?.jobId) window.dispatchEvent(new CustomEvent('alphaqap:job-submitted', { detail: data }));
    return data;
  }
  return attempt(false);
}

// ── Market data (① Briefings, ② Screener, ③ OHLCV for backtest) ─────────
export const msaApi = {
  // Full stock universe for the Schedule-A exchanges. The backtest symbol
  // picker needs the REAL list even while LIVE.msa is false (it drives
  // what you can backtest), so this is always live with a mock fallback
  // only when the backend is unreachable.
  async universe(exchanges = []) {
    const q = exchanges.length ? `?exchanges=${encodeURIComponent(exchanges.join(','))}` : '';
    try {
      return await request(`/msa/universe${q}`);
    } catch (error) {
      if (import.meta.env.PROD) throw error;
      return import('../modules/Screener/screener.mock.js').then((m) => m.mockUniverse());
    }
  },
  async universeSummary() {
    try {
      return await request('/msa/universe-summary');
    } catch (error) {
      if (import.meta.env.PROD) throw error;
      const rows = await import('../modules/Screener/screener.mock.js').then((m) => m.mockUniverse());
      return { total: rows.length, exchanges: {}, supportedExchanges: ['NGX', 'NSE', 'JSE', 'GSE', 'EGX', 'BRVM'] };
    }
  },
  async quotes(symbols) {
    if (!LIVE.msa) return import('../modules/Briefings/briefings.mock.js').then((m) => m.mockQuotes(symbols));
    return request(`/msa/quotes?symbols=${encodeURIComponent(symbols.join(','))}`);
  },
  async movers(exchange) {
    if (!LIVE.msa) return import('../modules/Briefings/briefings.mock.js').then((m) => m.mockMovers(exchange));
    return request(`/msa/movers?exchange=${encodeURIComponent(exchange)}`);
  },
  async indices() {
    if (!LIVE.msa) return import('../modules/Briefings/briefings.mock.js').then((m) => m.mockIndices());
    return request('/msa/indices');
  },
  async marketStatus(exchange) {
    if (!LIVE.msa) return import('../modules/Briefings/briefings.mock.js').then((m) => m.mockStatus(exchange));
    return request(`/msa/status?exchange=${encodeURIComponent(exchange)}`);
  },
  async news(symbol) {
    if (!LIVE.msa) return import('../modules/Briefings/briefings.mock.js').then((m) => m.mockNews(symbol));
    return request(`/msa/news${symbol ? `?symbol=${encodeURIComponent(symbol)}` : ''}`);
  },
  async profiles(options = {}) {
    const normalized = typeof options === 'string' ? { exchange: options } : options;
    if (!LIVE.msa) return import('../modules/Screener/screener.mock.js').then((m) => m.mockProfiles(normalized.exchange));
    const params = new URLSearchParams();
    Object.entries(normalized).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '' && value !== 'ALL') params.set(key, value);
    });
    const q = params.toString();
    return request(`/msa/profiles${q ? `?${q}` : ''}`);
  },
  async corporateActions() {
    if (!LIVE.msa) return import('../modules/Briefings/briefings.mock.js').then((m) => m.mockCaCalendar());
    return request('/msa/corporate-actions');
  },
};

// ── Backtest (③) — full open_backtester research workspace ───────────────
// Route mapping per docs/mystocks/OBT_INTEGRATION_SPEC.md §2. Every `run`
// (rules|signals) and `optimize`/`montecarlo`/`walkforward`/`portfolio`
// call returns (or, live, will return) the NORMALIZED result shape:
// { jobId, status, equity:[{date,equity,benchmark?}],
//   metrics:{ overall:{...}, byYear:[{year,return,sharpe,maxDrawdown,trades}] },
//   trades:[...], meta:{engine,mode,device,demoData} }. The mock module
// mirrors that shape exactly so this seam is a pure plumbing swap when
// OBT_BASE_URL/OBT_API_KEY land server-side and LIVE.backtest flips true.
export const backtestApi = {
  async indicators() {
    if (!LIVE.backtest) return import('../modules/Backtest/backtest.mock.js').then((m) => m.mockIndicators());
    return request('/backtest/indicators');
  },
  async run(payload) {
    assertValidBacktestPeriods(payload);
    assertBacktestExecutionCosts(payload);
    if (!LIVE.backtest) return import('../modules/Backtest/backtest.mock.js').then((m) => m.mockRun(payload));
    return request('/backtest/run', { method: 'POST', body: payload, auth: true });
  },
  async optimize(payload) {
    assertValidBacktestPeriods(payload);
    assertBacktestExecutionCosts(payload);
    if (!LIVE.backtest) return import('../modules/Backtest/backtest.mock.js').then((m) => m.mockOptimize(payload));
    return request('/backtest/optimize', { method: 'POST', body: payload, auth: true });
  },
  async montecarlo(payload) {
    assertValidBacktestPeriods(payload);
    assertBacktestExecutionCosts(payload);
    if (!LIVE.backtest) return import('../modules/Backtest/backtest.mock.js').then((m) => m.mockMonteCarlo(payload));
    return request('/backtest/montecarlo', { method: 'POST', body: payload, auth: true });
  },
  async walkforward(payload) {
    assertValidBacktestPeriods(payload);
    assertBacktestExecutionCosts(payload);
    if (!LIVE.backtest) return import('../modules/Backtest/backtest.mock.js').then((m) => m.mockWalkforward(payload));
    return request('/backtest/walkforward', { method: 'POST', body: payload, auth: true });
  },
  async search(payload) {
    assertValidBacktestPeriods(payload);
    assertBacktestExecutionCosts(payload);
    if (!LIVE.backtest) return import('../modules/Backtest/backtest.mock.js').then((m) => m.mockSearch?.(payload));
    return request('/backtest/search', { method: 'POST', body: payload, auth: true });
  },
  async portfolio(payload) {
    assertValidBacktestPeriods(payload);
    assertBacktestExecutionCosts(payload);
    if (!LIVE.backtest) return import('../modules/Backtest/backtest.mock.js').then((m) => m.mockBacktestPortfolio(payload));
    return request('/backtest/portfolio', { method: 'POST', body: payload, auth: true });
  },
  async get(runId) {
    if (!LIVE.backtest) return import('../modules/Backtest/backtest.mock.js').then((m) => m.mockGet(runId));
    return request(`/backtest/${runId}`, { auth: true });
  },
  async cancel(runId) { return request(`/backtest/${encodeURIComponent(runId)}/cancel`, { method: 'POST', auth: true }); },
  // Server-side run history for the signed-in seat (persisted runs, newest
  // first). Full results are fetched per-run via get(runId).
  async history() {
    if (!LIVE.backtest) return import('../modules/Backtest/backtest.mock.js').then((m) => m.mockHistory?.() || []);
    return request('/backtest/history', { auth: true });
  },
  async creditBalance() {
    if (!LIVE.backtest) return import('../modules/Backtest/backtest.mock.js').then((m) => m.mockBalance());
    return request('/backtest/credit/balance', { auth: true });
  },
  async ledger() {
    if (!LIVE.backtest) return import('../modules/Backtest/backtest.mock.js').then((m) => m.mockLedger());
    return request('/backtest/ledger/mine', { auth: true });
  },
};

// ── ML research (CPU/GPU train + hyperparam sweep) ───────────────────────
// Maps to /api/research/train + /api/research/sweep → engine
// /v1/research/train + /v1/research/sweep. GPU (device:"cuda") requires a
// GPU-enabled seat — gated both in the ConfigPanel (toggle disabled with an
// upgrade hint) and defensively in the mock/backend (402 gpu_tier_required).
export const mlApi = {
  async train(payload) {
    assertValidBacktestPeriods(payload);
    if (!LIVE.research) return import('../modules/Backtest/backtest.mock.js').then((m) => m.mockMlTrain(payload));
    return request('/research/train', { method: 'POST', body: payload, auth: true });
  },
  async sweep(payload) {
    assertValidBacktestPeriods(payload);
    if (!LIVE.research) return import('../modules/Backtest/backtest.mock.js').then((m) => m.mockMlSweep(payload));
    return request('/research/sweep', { method: 'POST', body: payload, auth: true });
  },
};

// ── Billing (Schedule-D tiers + compute-credit top-ups via Cashfree) ─────
export const billingApi = {
  async plans() {
    return request('/billing/plans');
  },
  async checkout({ planId, topupId }) {
    return request('/billing/checkout', { method: 'POST', body: { planId, topupId }, auth: true });
  },
  async status(orderId) {
    return request(`/billing/status/${orderId}`, { auth: true });
  },
  async orders() {
    return request('/billing/orders', { auth: true });
  },
};

// ── Portfolio (③ construction) ───────────────────────────────────────────
export const portfolioApi = {
  async list() {
    if (!LIVE.portfolio) return import('../modules/Portfolio/portfolio.mock.js').then((m) => m.mockList());
    return request('/portfolio', { auth: true });
  },
  async create(payload) {
    if (!LIVE.portfolio) return import('../modules/Portfolio/portfolio.mock.js').then((m) => m.mockCreate(payload));
    return request('/portfolio', { method: 'POST', body: payload, auth: true });
  },
  async update(id, payload) {
    if (!LIVE.portfolio) return import('../modules/Portfolio/portfolio.mock.js').then((m) => m.mockUpdate(id, payload));
    return request(`/portfolio/${id}`, { method: 'PUT', body: payload, auth: true });
  },
  async remove(id) {
    if (!LIVE.portfolio) return import('../modules/Portfolio/portfolio.mock.js').then((m) => m.mockRemove(id));
    return request(`/portfolio/${id}`, { method: 'DELETE', auth: true });
  },
  async risk(holdings) {
    return request('/portfolio/risk', { method: 'POST', body: { holdings }, auth: true });
  },
};

// ── Research (④) ─────────────────────────────────────────────────────────
export const researchApi = {
  async list() {
    if (!LIVE.research) return import('../modules/Research/research.mock.js').then((m) => m.mockList());
    return request('/research');
  },
  async publish(payload) {
    if (!LIVE.research) return import('../modules/Research/research.mock.js').then((m) => m.mockPublish(payload));
    return request('/research', { method: 'POST', body: payload, auth: true });
  },
  async qualify() {
    if (!LIVE.research) return import('../modules/Research/research.mock.js').then((m) => m.mockQualify());
    return request('/research/qualify', { method: 'POST', body: {}, auth: true });
  },
};

// ── Strategy copier (Schedule B) — publish a strategy from your ledger,
// discover others' strategies, copy-and-run in Backtest Studio. Copying is
// one-click re-run of the exact spec — no execution (technology layer only).
export const strategyApi = {
  async list() {
    return request('/strategies');
  },
  async get(id) {
    return request(`/strategies/${id}`);
  },
  async copy(id) {
    return request(`/strategies/${id}/copy`, { method: 'POST', auth: true });
  },
  async publish(payload) {
    return request('/strategies', { method: 'POST', body: payload, auth: true });
  },
};
